import React, {ChangeEvent, useState} from 'react';
import {Flex} from "@chakra-ui/layout";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  useDisclosure, useToast
} from "@chakra-ui/react";
import Plus from "../../../images/teacher/plus.svg";
import {NavLink} from "react-router-dom";
import {useSelector} from "react-redux";
import {StateType} from "../../../store/store";
import axios from "axios";

function TeacherNotes() {
  const {data: {token}, courses: {courses}} = useSelector((state: StateType) => state.rootReducer.teacherReducer);
  const teacherToken = token.includes("Bearer") ? token.slice(17, (token.length - 2)) : token.slice(10, (token.length - 2));
  const {isOpen, onOpen, onClose} = useDisclosure();
  const [note, setNote] = useState<FileList | null>(null);
  const [noteDescription, setNoteDescription] = useState<string>("");
  const [noteName, setNoteName] = useState<string>("");
  const [courseId, setCourseId] = useState<string>("");
  const [noteError, setNoteError] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [loaded, setLoaded] = useState(0);
  const [noteDescriptionError, setNoteDescriptionError] = useState<string>("");
  const [noteNameError, setNoteNameError] = useState<string>("");
  const [courseIdError, setCourseIdError] = useState<string>("");
  const toast = useToast();

  async function handleNoteUpload(e: ChangeEvent<HTMLInputElement>) {
    console.log(e.target.files);
    let notes = e.target.files;
    let size = 3221225472; //3gb
    // check size and length
    if (notes === null) return;
    if (notes.length > 3) {
      console.log("notes more than 3");
      setFileError("you cannot upload more than 3 files")
      notes = null;
      return;
    }
    const noteTypes = ['image/png', 'image/jpeg', 'application/pdf', 'application/vnd.ms-powerpoint.presentation.macroEnabled.12', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/msword', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    for (let i = 0; i < notes.length; i++) {
      // compare note type, find doesn't match
      if (noteTypes.every(type => !(notes) || notes[i].type !== type)) {
        // create error message and assign to container
        setFileError(notes[i].type + ' is not a supported format');
        return notes = null;
      }
    }
    // check size
    for (let i = 0; i < notes.length; i++) {
      if (notes[i].size > size) {
        setFileError(notes[i].type + "is too large, please pick a smaller file");
        return notes = null;
      }
    }
    setFileError("")
    if (fileError !== "") {
      notes = null;
      return
    }
    setNote(notes);
  }

  async function handleSubmit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    if (noteName === "") {
      return setNoteNameError("Please type in a name for the note")
    }
    setNoteNameError("");
    if (noteName.length < 3 || noteName.length > 35) {
      return setNoteNameError("Ensure that the note name is not less than 3 or more than 35 characters")
    }
    setNoteNameError("");
    if (noteDescription === "") {
      return setNoteDescriptionError("Please type in a description for the note")
    }
    setNoteDescriptionError("");
    if (noteDescription.length < 10 || noteDescription.length > 100) {
      return setNoteDescriptionError("Ensure that the note description is not less than 10 or more than 100 characters")
    }
    setNoteDescriptionError("");
    if (courseId === "") {
      return setCourseIdError(" Please choose a course")
    }
    setCourseIdError("");
    if (note === null) {
      return setNoteError("Please choose a note to upload");
    }
    setNoteError("");
    const uploadData = new FormData();
    uploadData.append("noteName", noteName)
    uploadData.append("noteDescription", noteDescription)
    uploadData.append("courseId", courseId)
    for (let i = 0; i < note.length; i++) {
      uploadData.append(note[i].name, note[i])
    }
    const res = await axios.post("/user/teacher/notes-upload", uploadData, {
      headers: {
        'content-type': 'multipart/form-data',
        "Authorization": `Bearer ${teacherToken}`
      },
      onUploadProgress: progressEvent => {
        setLoaded(progressEvent.loaded / progressEvent.total * 100)
      }
    })
    console.log(res);
    toast({
      title: "Upload Status",
      description: `${res.data.message}`,
      status: "success",
      duration: 9000,
      isClosable: true,
    })
  }

  return (
    <Flex justifyContent={"center"} mt={10} flexDirection={"row"}>
      <Flex flexDirection={"column"}>
        <Button onClick={onOpen} bgColor={"white"} fontSize={"sm"} fontWeight={"semibold"} borderRadius={"2xl"}
                size={"lg"} mb={10}>
          <Image
            boxSize="30px"
            objectFit="cover"
            mr={"12px"}
            src={Plus}
            alt="add icon"
          />
          Notes
        </Button>

        <Link as={NavLink} to={"/tutor/notes/uploaded-notes"} p={4} boxShadow="2xl" bgColor={"brand.darkGreen"}
              textAlign={"center"} borderRadius={"20px"} fontWeight={"bold"}
              _hover={{color: "blue", textDecoration: "none"}}>
          Uploaded Notes
        </Link>
      </Flex>

      <Modal closeOnOverlayClick={true} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Upload Note</ModalHeader>
          <ModalCloseButton/>
          <ModalBody pb={6}>
            <form encType="multipart/form-data">
              <Alert d={fileError === "" ? "none" : "flex"} status="error">
                <AlertIcon/>
                <AlertTitle mr={2}>{fileError}</AlertTitle>
              </Alert>
              <FormControl isInvalid={noteNameError !== ""}>
                <FormLabel htmlFor={"noteName"} fontWeight={"semibold"}>Note Name</FormLabel>
                <Input type={"text"} required minLength={3} maxLength={35} value={noteName}
                       onChange={(e) => setNoteName(e.target.value)}/>
                <FormErrorMessage>{noteNameError}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3} isInvalid={noteDescriptionError !== ""}>
                <FormLabel htmlFor={"noteDescription"} fontWeight={"semibold"}>Note Description</FormLabel>
                <Input type={"text"} min={10} max={100} required value={noteDescription}
                       onChange={(e) => (setNoteDescription(e.target.value))}/>
                <FormErrorMessage>{noteDescriptionError}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3} isInvalid={courseIdError !== ""}>
                <FormLabel htmlFor={"noteCourse"} fontWeight={"semibold"}>Course</FormLabel>
                <Select required value={courseId} onChange={(e) => setCourseId(e.target.value)}
                        placeholder="Choose course to give note for">
                  {courses.map((course: any, index) => (
                    <option key={index} value={course.course_id}>{course.course_name}</option>
                  ))}
                </Select>
                <FormErrorMessage>{courseIdError}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={noteError !== null} my={5}>
                <FormLabel htmlFor={"note"} fontWeight={"semibold"}>Note/File</FormLabel>
                <input id={"note"} type="file" multiple onChange={(e) => handleNoteUpload(e)}/>
                <FormErrorMessage>{noteError}</FormErrorMessage>
              </FormControl>
              <Progress hasStripe value={loaded} max={100} mb={4}/>
              <Button type={"submit"} bgColor={"brand.darkGreen"} onClick={(e) => handleSubmit(e)}>upload
                note</Button>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default TeacherNotes;